import './App.css';

import { useIsFetching } from '@tanstack/react-query';
import { lazy, Suspense, useEffect } from 'react';
import videojs from 'video.js';

// Components
import SuspenseFallback from './components/Common/SuspenseFallback';
// Utilities & Libraries
import { VIDEO_COPILOT_SOURCE } from './constants/constants';
// Context
import { ManagedPlayerContext } from './context';
import { getAccessToken, getRandomId } from './lib/api-client';
import eventTracker from './lib/eventTracker/eventTracker';
import { useInitMetaDataApi } from './lib/playerMetaApi/useInitMetaDataApi';
import QueryClientWrapper from './lib/QueryClientWrapper';
import { selectPlayerParams, useSelector } from './lib/redux';
import { VideoCopilotProvider } from './modules/video-copilot/context/VideoCopilotContext';
import { extractSourceType } from './modules/video-copilot/utils/videoTypeDetection';
import { useInitPlayerParams } from './playerParams';
import { usePlayerParamsEffects } from './playerParams/playerParamsEffects';
import {
  inIframe,
  isValidPWDomain,
  sendMessageToServiceWorker,
  SERVICE_WORKER_MESSAGE_TYPE,
} from './services/utils';
import NotificationWrapper from './shared/core/notification/NotificationWrapper';
import { updateDevTokenFromCred } from './utility';
// Lazy-loaded Components
const EmbeddedVideoPlayerWrapper = lazy(
  () => import('./components/Video-Player/EmbeddedVideoPlayer/EmbeddedVideoPlayerWrapper'),
);
const ScheduleDetailsWrapperWithAds = lazy(
  () => import('./components/Video-Player/ScheduleDetailsWrapperWithAds'),
);

function App() {
  updateDevTokenFromCred(); // Ensure dev token is updated from creds.json in localhost environment
  useInitPlayerParams();
  usePlayerParamsEffects(); // playerParamsSideEffects handler
  useInitMetaDataApi();
  const isOttDetailsLoading = useIsFetching({ queryKey: ['ott-video-details'] });
  const isOttPlaylistLoading = useIsFetching({ queryKey: ['ott-playlist'] });
  const playerParams = useSelector(selectPlayerParams);

  console.log(`Updated App Version: ${process.env.REACT_APP_VERSION}`);
  // const [proView, setProView] = useState(false);
  // const [isDBReady, setIsDBReady] = useState<boolean>(false);
  // const [signedUrl, setSignedUrl] = useState<string>("");
  // const [proviewData, setProviewData] = useState<any>("");

  useEffect(() => {
    // Initialize service worker parameters
    setServiceWorkerParams();

    // Handle "ProView" event
    // const handleEvent = () => {
    //   setProView(true);
    // };

    // If conditions are met, track the player load event
    if (isValidPWDomain() && !inIframe()) {
      const params: any = new Proxy(new URLSearchParams(location.search), {
        // @ts-ignore
        get: (searchParams, prop) => searchParams.get(prop),
      });
      eventTracker.pw_player_load({ schedule_id: params['scheduleId'] || '' });
    }
    const handleIframeListener = (event: any) => {
      if (event?.data?.action === 'user_details') {
        localStorage.setItem('user', JSON.stringify(event?.data?.data));
      }
    };

    window.addEventListener('message', handleIframeListener);
    // Add event listener for ProView
    // window.addEventListener("ProView", handleEvent);

    // Initialize the database and set preference in local storage
    // handleInitDB();
    localStorage.setItem('Preference_New_Player', 'YES');

    // Clear old video data
    localStorage.removeItem('oldWatchStats');

    // Cleanup function to remove the event listener
    return () => {
      window.removeEventListener('message', handleIframeListener);
      // window.removeEventListener("ProView", handleEvent);
    };
  }, []);

  // useEffect(() => {
  //   let interval;
  //   clearInterval(interval);
  //   if (signedUrl.length > 0) {
  //     interval = setInterval(() => {
  //       getData(signedUrl);
  //     }, 1000 * 60 * 2);
  //   }

  //   return clearInterval(signedUrl);
  // }, [signedUrl]);

  // const handleInitDB = async () => {
  //   const status = await initDB();
  //   //SessionEventreset();
  //   setIsDBReady(status);
  // };

  const setServiceWorkerParams = () => {
    const browser = videojs?.browser;
    const isSafari =
      browser &&
      (browser['IS_SAFARI'] ||
        browser['IS_IOS'] ||
        browser['IS_IPAD'] ||
        browser['IS_IPHONE'] ||
        browser['IS_IPOD']);

    sendMessageToServiceWorker(SERVICE_WORKER_MESSAGE_TYPE.UPDATE_IS_SAFARI, isSafari);

    if (isSafari) {
      sendMessageToServiceWorker(SERVICE_WORKER_MESSAGE_TYPE.UPDATE_HEADERS, {
        randomId: getRandomId(),
        authorization: getAccessToken(),
      });
    }
  };

  // const proviewMatrixToServer = () => {
  //   const url = `${process.env.REACT_APP_VP_BACKEND}/get-signed-url?filename=${
  //     new Date().toISOString().split("T")[0]
  //   }/${params?.scheduleId}-${new Date().getTime()}.csv`;
  //   // fetch()
  //   fetch(url, {
  //     method: "GET", // *GET, POST, PUT, DELETE, etc.
  //     // headers: {
  //     //     'Content-Type': 'application/json',
  //     // },
  //   })
  //     .then((d) => {
  //       //@ts-ignore
  //       console.log("response", d);
  //       return d.json();
  //     })
  //     .then((r) => {
  //       console.log(">>>>", r);
  //       setSignedUrl(r.signedUrl);
  //     });
  // };

  // const getData = async (url: string) => {
  //   const userInfo = JSON.parse(localStorage.getItem("user") as string);
  //   if (url.length > 0) {
  //     const events: any[] = await exportStore();
  //     const Data = {
  //       events,
  //       location: window.location.href,
  //       userAgent: navigator.userAgent,
  //       token: localStorage.getItem("TOKEN") || localStorage.getItem("token"),
  //       randomId:
  //         localStorage.getItem("randomId") || localStorage.getItem("uuid"),
  //       user: localStorage.getItem("user"),
  //       timestamp: new Date(),
  //     };

  //     let csvArray: any = [];
  //     let csvHeader =
  //       "id|userId|batchId|scheduleId|type|description|time_stamp|video_state|data";
  //     csvArray.push(csvHeader);
  //     events.forEach((el) => {
  //       let value = [
  //         el.id,
  //         userInfo?.id,
  //         params?.batchSlug,
  //         params?.scheduleId,
  //         el.value.type,
  //         el.value.data.description,
  //         el.value.data.timestamp.replace(",", ""),
  //         el.value.data.videoState,
  //         JSON.stringify(el.value.data.data),
  //       ].join("|");
  //       csvArray.push(value);
  //     });
  //     let csvFile = csvArray.join("\n");
  //     const blob = new Blob([csvFile], { type: "data:text/csv;charset=utf-8" });
  //     const dataUrl = URL.createObjectURL(blob);
  //     fetch(url, {
  //       method: "PUT",
  //       body: blob,
  //     }).then((D) => {
  //       console.log("RESPONSE", D);
  //     });
  //     setProviewData(blob);
  //   }
  // };

  const isAnyRequiredParamMissing = !(
    playerParams.scheduleId ||
    playerParams.contentId ||
    playerParams.type ||
    playerParams.src
  );

  if (isOttDetailsLoading || isOttPlaylistLoading || isAnyRequiredParamMissing) {
    return <SuspenseFallback />;
  }

  const isEmbeddedMode = playerParams.src && playerParams.type;

  const extractedSourceType = extractSourceType({
    scheduleId: playerParams.scheduleId,
    contentId: playerParams.contentId,
    khazanaProgramId: playerParams.khazanaProgramId,
    srcType: playerParams.type,
    VIDEO_COPILOT_SOURCE,
  });

  const shouldUseEnhancedEmbedded =
    isEmbeddedMode && extractedSourceType === VIDEO_COPILOT_SOURCE.KHAZANA;

  return (
    <QueryClientWrapper>
      <ManagedPlayerContext {...playerParams}>
        <VideoCopilotProvider {...playerParams}>
          <NotificationWrapper />
          {playerParams.src && playerParams.type ? (
            <Suspense fallback={<SuspenseFallback />}>
              <EmbeddedVideoPlayerWrapper />
            </Suspense>
          ) : (
            <>
              <Suspense fallback={<SuspenseFallback />}>
                <ScheduleDetailsWrapperWithAds />
              </Suspense>
            </>
          )}
        </VideoCopilotProvider>
      </ManagedPlayerContext>
    </QueryClientWrapper>
  );
}

export default App;
